import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Search } from "lucide-react";
import React from "react";

import { Input } from "../../components/ui/input";
import Layout from "./Layout";

const ArticleItem = ({ title, description, link, imageUrl }) => {
	return (
		<div className="bg-white rounded-lg shadow p-4">
			{imageUrl && <img src={imageUrl} alt={title} className="w-full rounded h-48 object-cover mb-4" />}

			<h2 className="text-lg font-bold mb-2">{title}</h2>
			<p className="text-gray-700 mb-4">{description}</p>
			<a href={link} className="text-blue-500 hover:underline flex items-center leading-none justify-end py-2">
				Learn More <ChevronRight size={16} className="inline" />
			</a>
		</div>
	);
};

function BlogCategories() {
	const { data, isLoading } = useQuery({
		queryKey: ["blog-categories"],
		queryFn: async () => {
			const postsRes = await fetch("https://dproz.joeeverest.space/wp-json/wp/v2/posts?categories=3");
			if (!postsRes.ok) {
				throw new Error(`Failed to fetch posts: ${postsRes.status}`);
			}
			const posts = await postsRes.json();

			const postsWithImages = await Promise.all(
				posts.map(async (post) => {
					let imageUrl = null;
					if (
						post._links &&
						post._links["wp:featuredmedia"] &&
						post._links["wp:featuredmedia"][0] &&
						post._links["wp:featuredmedia"][0].href
					) {
						try {
							const mediaRes = await fetch(post._links["wp:featuredmedia"][0].href);
							if (mediaRes.ok) {
								const mediaData = await mediaRes.json();
								imageUrl = mediaData.source_url; // Standard field for full image URL
							}
						} catch (error) {
							// Error handling can be improved based on requirements
						}
					}
					return { ...post, imageUrl };
				}),
			);
			return postsWithImages;
		},
	});

	return (
		<Layout>
			<section className="max-w-7xl mx-auto px-4 md:px-8 py-16 bg-white">
				<h1 className="font-bold text-5xl font-plus-jakarta">Explore Careers</h1>
				<p className="font-inter text-[#333333] text-xl mt-4">
					Discover career opportunities across various fields. Dive into role explainers to learn about
					responsibilities, required skills, and potential career paths.
				</p>
			</section>
			<section className=" bg-gray-50">
				<div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
					<div className="*:not-first:mt-2">
						<div className="relative">
							<Input className="peer ps-9 max-w-2xl bg-white" placeholder="Search" type="text" />
							<div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
								<Search size={16} aria-hidden="true" />
							</div>
						</div>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
						{isLoading && (
							<>
								{new Array(3).fill(0).map((_, index) => (
									<div className="bg-white rounded-lg shadow p-4 animate-pulse" key={index}>
										<div className="w-full rounded h-48 bg-gray-200 mb-4" />
										<div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
										<div className="h-4 bg-gray-200 rounded w-full mb-4" />
										<div className="h-4 bg-gray-200 rounded w-1/4 mb-32" />
									</div>
								))}
							</>
						)}
						{data?.map((item) => (
							<ArticleItem
								key={item.id}
								title={item.title.rendered}
								imageUrl={item.imageUrl}
								description={
									item.excerpt.rendered
										.replace(/<[^>]+>/g, "")
										.slice(0, 240)
										.trim() + "..."
								}
								link={"/resources/" + item.slug}
							/>
						))}
					</div>
				</div>
			</section>
		</Layout>
	);
}

export default BlogCategories;
